import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "isFancy", "max", "min", "onChange", "onEnd", "onStart", "options", "step", "testID", "UNSAFE_containerProps", "value", "width"];
import React, { useCallback, useMemo } from 'react';
import { DesignContextForwardProvider, useDesignContext } from '@klarna/designtokens';
import withProvisionalOverrideFromContext from '@bubble-ui/hoc-with-provisional-override-from-context';
import ActionIconChevronLeft from '@bubble-ui/icon-action-chevron-left';
import ActionIconChevronRight from '@bubble-ui/icon-action-chevron-right';
import View from '@bubble-ui/primitive-view';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { defaultDesignTokens } from './defaultDesignTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
import { getIds } from './getIds';
import { calculatePosition, calculatePositionChange, calculateStepPx, getFancyState, hasOptions, useMinAndMax } from './utils';
var SliderComponent = _ref => {
  var {
      id,
      isFancy,
      max: maxFromUser,
      min: minFromUser,
      onChange,
      onEnd,
      onStart,
      options,
      step,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      testID,
      UNSAFE_containerProps,
      value = 0,
      width
      /**
       * @deprecated props spreading is outdated and will be removed with v7.0.0
       */
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  usePropsSpreadingDeprecationWarning({
    componentName: 'Slider',
    props,
    useInstead: 'UNSAFE_containerProps',
    removalDate: 'v7.0.0'
  });
  var designContext = useDesignContext(defaultDesignTokens);
  var ids = getIds(id);
  var {
    min,
    max
  } = useMinAndMax({
    minFromUser,
    maxFromUser,
    options
  });
  var fancyState = getFancyState(isFancy);
  var knobSize = designContext.get('slider/knob-size', {
    fancyState
  }).value();
  var stepPx = useMemo(() => calculateStepPx({
    knobSize,
    max,
    width
  }), [knobSize, max, width]);
  var position = useMemo(() => calculatePosition({
    value,
    stepPx
  }), [value, stepPx]);
  var onPositionChange = useCallback(x => calculatePositionChange({
    knobSize,
    max,
    min,
    onChange,
    options,
    step,
    stepPx,
    value,
    x
  }), [knobSize, max, min, onChange, options, step, stepPx, value]);
  var onTouchStart = useCallback(e => {
    if (e.touches.length === 1) {
      var {
        clientX
      } = e.touches[0];
      var leftOffset = e.currentTarget.getBoundingClientRect().left;
      onStart && onStart();
      onPositionChange(clientX - leftOffset);
      var _onTouchMove = e => {
        // ignore multi-touch gestures
        if (e.touches.length === 1) {
          var {
            clientX: _clientX
          } = e.touches[0];
          onPositionChange(_clientX - leftOffset);
        }
      };
      var onTouchEnd = () => {
        onEnd && onEnd();
        global.removeEventListener('touchmove', _onTouchMove);
        global.removeEventListener('touchend', onTouchEnd);
        global.removeEventListener('touchcancel', onTouchEnd);
      };
      global.addEventListener('touchmove', _onTouchMove, false);
      global.addEventListener('touchend', onTouchEnd, false);
      global.addEventListener('touchcancel', onTouchEnd, false);
    }
  }, [onEnd, onPositionChange, onStart]);

  // prevent scroll
  var onTouchMove = useCallback(e => e.preventDefault(), []);
  var onMouseDown = useCallback(e => {
    var leftOffset = e.currentTarget.getBoundingClientRect().left;
    onStart && onStart();
    onPositionChange(e.clientX - leftOffset);
    var onMouseMove = e => {
      onPositionChange(e.clientX - leftOffset);
    };
    var onMouseUp = () => {
      onEnd && onEnd();
      global.removeEventListener('mousemove', onMouseMove);
      global.removeEventListener('mouseup', onMouseUp);
    };
    global.addEventListener('mousemove', onMouseMove, false);
    global.addEventListener('mouseup', onMouseUp, false);
  }, [onEnd, onPositionChange, onStart]);
  var onKeyDown = useCallback(_ref2 => {
    var {
      key
    } = _ref2;
    switch (key) {
      case 'ArrowRight':
        {
          var nextValue = value;

          // non-linear
          if (options && hasOptions(options)) {
            var currentIndex = options.indexOf(value);
            nextValue = options[currentIndex + 1];
            // linear
          }

          if (step) {
            var steps = Math.floor(value / step);
            nextValue = (steps + 1) * step;
          }
          if (onChange) {
            if (nextValue <= max) {
              onChange(nextValue);
            }
          }
          break;
        }
      case 'ArrowLeft':
        {
          var _nextValue = value;

          // non-linear
          if (options && hasOptions(options)) {
            var _currentIndex = options.indexOf(value);
            _nextValue = options[_currentIndex - 1];
            // linear
          }

          if (step) {
            var _steps = Math.floor(value / step);
            _nextValue = (_steps - 1) * step;
          }
          if (onChange) {
            if (_nextValue >= min) {
              onChange(_nextValue);
            }
          }
          break;
        }
    }
  }, [min, onChange, max, step, options, value]);
  var {
    iconStyle,
    knobStyle,
    leftBarStyle,
    leftChevronStyle,
    rightBarStyle,
    rightChevronStyle,
    rootStyle
  } = getDefaultStyleSheet({
    designContext,
    isFancy,
    position,
    width
  });
  return /*#__PURE__*/React.createElement("div", _extends({}, UNSAFE_containerProps, {
    tabIndex: 0
  }, props, {
    onTouchStart: onTouchStart,
    onTouchMove: onTouchMove,
    onMouseDown: onMouseDown,
    onKeyDown: onKeyDown,
    style: rootStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: leftBarStyle
  }), /*#__PURE__*/React.createElement(View, {
    style: rightBarStyle
  }), /*#__PURE__*/React.createElement(View, {
    id: ids === null || ids === void 0 ? void 0 : ids.knob,
    style: knobStyle
  }, /*#__PURE__*/React.createElement(View, {
    style: leftChevronStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(ActionIconChevronLeft, iconStyle))), /*#__PURE__*/React.createElement(View, {
    style: rightChevronStyle
  }, /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(ActionIconChevronRight, iconStyle)))));
};
SliderComponent.displayName = 'Slider';
var Slider = withProvisionalOverrideFromContext(SliderComponent);
export default Slider;
//# sourceMappingURL=index.js.map